// All reading AI types. Controls right-click behavior (opens detection image instead of label crop),
// metadata extraction, canvas selectedLabel passing, zoom/split-view layout, and stage tab visibility.
export const READING_AI_TYPES = [
  'Label Reading',
  'Bay Label Reading',
  'Overhead Inventory Box Reading',
  'Promotional Signage Reading',
];

// Reading AI types that operate on shelf labels (text, price, bay labels).
// Overhead Inventory Box Reading is intentionally excluded — it counts boxes, not labels.
export const SHELF_READING_AI_TYPES = [
  'Label Reading',
  'Bay Label Reading',
  'Promotional Signage Reading',
];

// Reading AI types whose detections carry a layout classification (layout_id).
// Label Reading applies to both Shelf and Overhead object types.
export const LAYOUT_ID_AI_TYPES = [
  'Label Reading',
  'Bay Label Reading',
  'Promotional Signage Reading',
];

// AI types that have a collector/source field.
export const COLLECTOR_AI_TYPES = [
  'Label Detection',
  'Label Reading',
  'Product Detection',
  'Promotional Signage Detection',
  'Promotional Signage Reading',
  'Bay Label Detection',
  'Bay Label Reading',
];

export const PREDICTION_TYPE_DISPLAY_COLORS = {
  0: '#fbd460',
  1: '#76fc62',
  2: '#ff7596',
  3: '#44c3fa',
  4: '#fbd460',
  5: '#ff8035',
  6: '#ad9dfc',
  7: '#000000',
  'item': '#fbd460',
  'correlative': '#ad9dfc',
  'ean': '#76fc62',
  'quantity': '#ad9dfc',
  'promotion': '#ff8035',
  'bay': '#ff7596',
  'sidecap': '#fbd460',
  'pallet': '#44c3fa',
  'shelf': '#ff7596',
}

export const PREDICTION_TYPE_DISPLAY_NAME = {
  0: 'Price',
  1: 'EAN',
  2: 'Item',
  3: 'Text',
  4: 'Barcode',
  5: 'Label type',
  6: 'Quantity',
  7: 'Date',
  8: 'Brand',
  9: 'Correlative',
  10: 'Promotion',
  11: 'Start date',
  12: 'End date',
  13: 'Promotion code',
  14: 'Unit type',
  "shelf": "Shelf",
  'item': 'Item',
  'correlative': 'Correlative',
  'ean': 'EAN',
  'quantity': 'Quantity',
  'date': 'Date',
  'promotion': 'Promotion',
  'bay': 'Bay',
  'sidecap': 'Sidecap',
  'pallet': 'Pallet',
}

const FIXTURE_DISPLAY_NAME = {
  0: 'On shelf',
  1: 'Hook',
  2: 'On floor',
  3: 'Cut case',
  5: 'Dispenser',
  6: 'Under shelf',
  9: 'Haligned basket',
  10: 'Sample display',
}

export const LABEL_DISPLAY = {
  detection: {
    order: 0,
    color: "#2980b9",
    display_name: "Detection",
    size: [40, 40],
  },
  prediction: {
    order: 1,
    color: "#e74c3c",
    display_name: "Prediction",
    size: [35, 35],
  },
  consolidation: {
    order: 2,
    color: "#2ecc71",
    display_name: "Consolidation",
    size: [30, 30],
  },
  seen: {
    order: 3,
    color: "#f39c12",
    display_name: "Read",
    size: [25, 25],
  },
  audit_false_positive: {
    order: 5,
    color: "#58B19F",
    display_name: "Audit FP",
    size: [20, 20],
  },
  audit_false_negative: {
    order: 6,
    color: "#25CCF7",
    display_name: "Audit FN",
    size: [20, 20],
  }
};

export const consolidationStagesOptions = [
  { order: 0, name: 'detection', label: 'Detection', color: '#2980b9' },
  { order: 1, name: 'prediction', label: 'Prediction', color: '#e74c3c' },
  { order: 2, name: 'consolidation', label: 'Consolidation', color: '#2ecc71' },
  { order: 3, name: 'seen', label: 'Match', color: '#f39c12' }
];


const pastelColorForKey = key => {
  const str = String(key ?? '');
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
  }
  const hue = Math.abs(hash) % 360;
  const s = 0.70;
  const l = 0.70;
  const h = hue / 360;

  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = hue2rgb(p, q, h + 1 / 3);
  const g = hue2rgb(p, q, h);
  const b = hue2rgb(p, q, h - 1 / 3);

  const toHex = x => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}


export const handleGetPredictionsColor = ({ predictions, stage, possibleStages = ['text_recognition', 'text_recognition_postprocessed', 'results', 'second_stage'] }) => {
  const isFixture = predictions.some(prediction => prediction.fixture != null);
  const typographyLabelsSet = new Set();

  predictions.forEach(({ prediction_type, class: classType, fixture, reading_type, reading_stage, second_stage_reading }) => {
    const targetPredictionType = isFixture ? fixture : prediction_type ?? classType;
    if (stage === "second_stage" && second_stage_reading) {
      typographyLabelsSet.add(targetPredictionType);
    } else if (stage === reading_stage) {
      typographyLabelsSet.add(reading_stage);
    } else if (['text_recognition', 'text_recognition_postprocessed'].includes(stage)) {
      return;
    } else if (!possibleStages.includes(targetPredictionType)) {
      typographyLabelsSet.add(targetPredictionType);
    }
  });

  const toTitleCase = str => str != null ? String(str).split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : null;
  const predictionColors = Array.from(typographyLabelsSet).reduce((acc, label) => {
    const predictionName = (isFixture ? FIXTURE_DISPLAY_NAME[label] : PREDICTION_TYPE_DISPLAY_NAME[label]) || toTitleCase(label);
    if (predictionName) {
      acc[label] = {
        name: predictionName,
        color: PREDICTION_TYPE_DISPLAY_COLORS[label] || pastelColorForKey(label),
        id: label,
      }
    }
    return acc;
  }, {})


  return predictionColors
}

// Draw functions accept either a canvas id or a canvas element.
const resolveCanvas = canvas => (typeof canvas === 'string' ? document.getElementById(canvas) : canvas);

export const drawLabels = async (metadata, imagecanvas, theme, factor = 1, selectedLabel, showVertexNumbers = false) => {
  const canvas = resolveCanvas(imagecanvas);
  const ctx = canvas.getContext('2d');

  metadata.forEach(label => {
    const bboxPolygon = label.x1 !== undefined && label.y1 !== undefined && label.x2 !== undefined && label.y2 !== undefined
      ? [
        [label.x1, label.y1],
        [label.x1, label.y2],
        [label.x2, label.y2],
        [label.x2, label.y1],
      ]
      : null;

    ctx.lineWidth = '6';
    ctx.strokeStyle = label.strokeStyle ?? theme.palette.secondary.main;

    if (bboxPolygon && (!selectedLabel || (selectedLabel && label.detection_id === selectedLabel))) {
      drawPolygonWithVertexLabels(ctx, bboxPolygon, factor, { showVertexNumbers });
    }

    ctx.font = '100% ' + theme.typography.fontFamily;
    ctx.fillStyle = theme.palette.secondary.main;

    if (label.score && (!selectedLabel || (selectedLabel && label.detection_id === selectedLabel))) {
      ctx.fillText(
        (label.score * 100).toFixed(2) + '%',
        label.x1 * factor,
        label.y1 * factor - 5,
      );
    }
  });
};

export const SHELFOUT_POSITION_SOURCES = {
  ALGORITHM: 'position_algorithm',
  PREDICTION: 'position_prediction',
};

export const SHELFOUT_POSITION_SCOPES = {
  SHELFOUTS: 'shelfouts',
  ALL: 'all',
};

export const filterShelfoutPositions = (positions = [], source, scope) => positions.filter(
  position => position.source === source
    && (scope === SHELFOUT_POSITION_SCOPES.ALL || Boolean(position.stock_alert)),
);

const drawScoreLabel = (ctx, score, x, y, color, theme) => {
  ctx.font = '100% ' + theme.typography.fontFamily;
  ctx.fillStyle = color;
  ctx.fillText((score * 100).toFixed(2) + '%', x, y - 5);
};

export const drawFacings = async (metadata, imagecanvas, theme, factor = 1, predictionColors = {}, showVertexNumbers = false) => {
  const canvas = resolveCanvas(imagecanvas);
  const ctx = canvas.getContext('2d');

  metadata.forEach(position => {
    const color = predictionColors[position.class ?? position.fixture]?.color || theme.palette.secondary.main;
    ctx.strokeStyle = color;
    ctx.lineWidth = '8';

    if (position.source === SHELFOUT_POSITION_SOURCES.PREDICTION) {
      drawPolygonWithVertexLabels(ctx, position.bbox, factor, { showVertexNumbers });

      if (position.stock_alert_score != null) {
        const left = Math.min(...position.bbox.map(([x]) => x));
        const top = Math.min(...position.bbox.map(([, y]) => y));
        drawScoreLabel(ctx, position.stock_alert_score, left * factor, top * factor, color, theme);
      }
      return;
    }

    if (position.prediction_bbox?.length !== 4) return;

    drawPolygonWithVertexLabels(ctx, [
      [position.prediction_bbox[0], position.prediction_bbox[1]],
      [position.prediction_bbox[0], position.prediction_bbox[3]],
      [position.prediction_bbox[2], position.prediction_bbox[3]],
      [position.prediction_bbox[2], position.prediction_bbox[1]],
    ], factor, { showVertexNumbers });

    if (position.prediction_score) {
      drawScoreLabel(
        ctx,
        position.prediction_score,
        position.prediction_bbox[0] * factor,
        position.prediction_bbox[1] * factor,
        color,
        theme,
      );
    }
  });
};

const getPointDistance = (pointA, pointB, factor = 1) => Math.sqrt(
  ((pointB[0] - pointA[0]) * factor) ** 2
  + ((pointB[1] - pointA[1]) * factor) ** 2,
);

const getBboxTextTransform = (bbox_coords, factor = 1) => {
  if (!Array.isArray(bbox_coords) || bbox_coords.length !== 4) return null;
  if (!bbox_coords.every(point => Array.isArray(point) && point.length === 2)) return null;

  const originIdx = 0;
  const textAxisIdx = 1;
  const crossAxisIdx = bbox_coords.length - 1;

  return {
    originIdx,
    textAxisIdx,
    crossAxisIdx,
    angle: Math.atan2(
      (bbox_coords[textAxisIdx][1] - bbox_coords[originIdx][1]) * factor,
      (bbox_coords[textAxisIdx][0] - bbox_coords[originIdx][0]) * factor,
    ),
    textLength: getPointDistance(bbox_coords[originIdx], bbox_coords[textAxisIdx], factor),
    crossLength: getPointDistance(bbox_coords[originIdx], bbox_coords[crossAxisIdx], factor),
    originX: bbox_coords[originIdx][0] * factor,
    originY: bbox_coords[originIdx][1] * factor,
    endX: bbox_coords[textAxisIdx][0] * factor,
  };
};

/**
 * Computes the transform needed to render a single-line text inside a bbox.
 *
 * IMPORTANT: this helper is intentionally pure. It only returns transform and
 * scaling data derived from the incoming vertex order. Callers remain
 * responsible for wrapping drawing in save/restore and for applying
 * translate/rotate/scale/fillText in one explicit block.
 */
const getSingleLineTextInBboxTransform = (ctx, bbox_coords, text, factor, yOffset = 0) => {
  const bboxTextTransform = getBboxTextTransform(bbox_coords, factor);
  if (!bboxTextTransform || typeof text !== 'string' || text === '') return null;

  const {
    angle,
    originX,
    originY,
    textLength,
    crossLength,
  } = bboxTextTransform;

  const metrics = ctx.measureText(text);
  const textWidth = metrics.width;
  const realTextHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
  if (!textWidth || !realTextHeight) return null;

  const scaleX = textLength / textWidth;
  const scaleY = crossLength / realTextHeight;

  return {
    ...bboxTextTransform,
    angle,
    originX,
    originY: originY + yOffset,
    realTextHeight,
    scaleX,
    scaleY,
  };
};

const drawPolygonPath = (ctx, polygon, factor = 1) => {
  if (!Array.isArray(polygon) || polygon.length === 0) return;

  ctx.beginPath();
  ctx.moveTo(polygon[0][0] * factor, polygon[0][1] * factor);

  for (let index = 1; index < polygon.length; index++) {
    ctx.lineTo(polygon[index][0] * factor, polygon[index][1] * factor);
  }

  ctx.closePath();
};

const drawPolygonWithVertexLabels = (ctx, polygon, factor = 1, { showVertexNumbers = false } = {}) => {
  drawPolygonPath(ctx, polygon, factor);
  ctx.stroke();
  if (showVertexNumbers) {
    drawPolygonVertexLabels(ctx, polygon, factor);
  }
};

const drawPolygonVertexLabels = (ctx, polygon, factor = 1) => {
  if (!Array.isArray(polygon) || polygon.length === 0) return;

  ctx.save();
  ctx.font = '12px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  polygon.forEach((point, index) => {
    const x = point[0] * factor;
    const y = point[1] * factor;
    const label = String(index + 1);
    const paddingX = 4;
    const paddingY = 2;
    const metrics = ctx.measureText(label);
    const textWidth = metrics.width;
    const textHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent || 12;

    ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
    ctx.fillRect(
      x - (textWidth / 2) - paddingX,
      y - (textHeight / 2) - paddingY,
      textWidth + paddingX * 2,
      textHeight + paddingY * 2,
    );

    ctx.fillStyle = 'rgba(255, 255, 255, 0.72)';
    ctx.fillText(label, x, y);
  });

  ctx.restore();
};

/**
 * Renders the title text inside the results bbox.
 * Uses parsed_data (postprocessed) only to infer line breaks and line spacing.
 * Falls back to greedy width-wrap if word matching fails.
 * Uses the incoming bbox vertex order as the text orientation source.
 *
 * @param {CanvasRenderingContext2D}   ctx
 * @param {number[][]}                 bbox_coords    4-point quad from results.
 * @param {string}                     value          Title string.
 * @param {Map<string, number[][][]>}  wordBboxIndex  text → bbox list from parsed_data.
 * @param {number}                     factor         Canvas scale factor.
 */
const drawTitleInBbox = (ctx, bbox_coords, value, wordBboxIndex, factor) => {
  if (typeof value !== 'string' || value.trim() === '') return;

  const bboxTextTransform = getBboxTextTransform(bbox_coords, factor);
  if (!bboxTextTransform) return;

  const {
    angle,
    originX,
    originY,
    textLength: bboxWidth,
    crossLength: bboxHeight,
  } = bboxTextTransform;

  const words = value.split(' ');
  const sampleMetrics = ctx.measureText('M');
  const glyphHeight = sampleMetrics.actualBoundingBoxAscent + sampleMetrics.actualBoundingBoxDescent;
  const lineHeight = glyphHeight * 1.2;

  const wrapToWidth = maxWidth => {
    const lines = [];
    let currentLine = '';
    for (const word of words) {
      const proposed = currentLine ? `${currentLine} ${word}` : word;
      if (currentLine && ctx.measureText(proposed).width > maxWidth) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = proposed;
      }
    }
    if (currentLine) lines.push(currentLine);
    return lines;
  };

  const titleMinX = Math.min(...bbox_coords.map(p => p[0]));
  const titleMaxX = Math.max(...bbox_coords.map(p => p[0]));
  const titleMinY = Math.min(...bbox_coords.map(p => p[1]));
  const titleMaxY = Math.max(...bbox_coords.map(p => p[1]));

  const usedPerWord = new Map();
  const wordBoxMatches = words.map(w => {
    const boxes = wordBboxIndex.get(w);
    if (!Array.isArray(boxes)) return null;
    const startIdx = usedPerWord.get(w) || 0;
    for (let i = startIdx; i < boxes.length; i++) {
      const wb = boxes[i];
      if (!wb || wb.length !== 4) continue;
      const cx = (wb[0][0] + wb[2][0]) / 2;
      const cy = (wb[0][1] + wb[2][1]) / 2;
      if (cx >= titleMinX && cx <= titleMaxX && cy >= titleMinY && cy <= titleMaxY) {
        usedPerWord.set(w, i + 1);
        return wb;
      }
    }
    return null;
  });

  const allWordsMatched = wordBoxMatches.length > 0
    && wordBoxMatches.every(b => b && b.length === 4);

  let bestLines;
  let effectiveLineHeight = lineHeight;
  if (allWordsMatched) {
    const cosA = Math.cos(-angle);
    const sinA = Math.sin(-angle);
    const wordYs = wordBoxMatches.map(wb => {
      const cx = (wb[0][0] + wb[2][0]) / 2;
      const cy = (wb[0][1] + wb[2][1]) / 2;
      return cx * sinA + cy * cosA;
    });
    const lineThreshold = glyphHeight * 0.5;
    const groups = [[words[0]]];
    const groupAnchorYs = [wordYs[0]];
    let currentAnchor = wordYs[0];
    for (let i = 1; i < words.length; i++) {
      if (Math.abs(wordYs[i] - currentAnchor) > lineThreshold) {
        groups.push([words[i]]);
        groupAnchorYs.push(wordYs[i]);
        currentAnchor = wordYs[i];
      } else {
        groups[groups.length - 1].push(words[i]);
      }
    }
    bestLines = groups.map(g => g.join(' '));

    if (groupAnchorYs.length > 1) {
      const gaps = [];
      for (let i = 1; i < groupAnchorYs.length; i++) {
        gaps.push((groupAnchorYs[i] - groupAnchorYs[i - 1]) * factor);
      }
      const avgGap = gaps.reduce((a, b) => a + b, 0) / gaps.length;
      if (avgGap > glyphHeight) {
        effectiveLineHeight = avgGap;
      }
    }
  } else {
    bestLines = wrapToWidth(bboxWidth);
  }

  const widestLineWidth = bestLines.reduce(
    (max, line) => Math.max(max, ctx.measureText(line).width),
    0,
  );
  const totalTextHeight = effectiveLineHeight * bestLines.length;
  if (widestLineWidth === 0 || totalTextHeight === 0 || bboxWidth === 0 || bboxHeight === 0) return;
  const scaleX = bboxWidth / widestLineWidth;
  const scaleY = bboxHeight / totalTextHeight;

  ctx.translate(originX, originY);
  ctx.rotate(angle);
  ctx.scale(scaleX, scaleY);
  ctx.textBaseline = 'top';
  for (let i = 0; i < bestLines.length; i++) {
    ctx.fillText(bestLines[i], 0, i * effectiveLineHeight);
  }
};

export const drawLabelReadings = async (metadata, currencyFormat, format, imagecanvas, theme, disableText, showLabelText, factor = 1, readingCanvas, chain, splitView, stage = "results", predictionColors = {}, showVertexNumbers = false) => {
  const {
    currency_symbol,
    currency_thousands_sep,
    currency_decimal_sep,
    currency_decimals,
  } = currencyFormat;
  const canvas = resolveCanvas(imagecanvas);
  const ctx = canvas.getContext('2d');
  const margin = 10;

  const wordBboxIndex = new Map();
  metadata.forEach(label => {
    if (
      (label.reading_stage === 'text_recognition_postprocessed'
        || label.reading_stage === 'text_recognition')
      && Array.isArray(label.parsed_data)
    ) {
      label.parsed_data.forEach(item => {
        if (item?.texts && Array.isArray(item.bboxes)) {
          const existing = wordBboxIndex.get(item.texts) || [];
          existing.push(item.bboxes);
          wordBboxIndex.set(item.texts, existing);
        }
      });
    }
  });

  metadata.forEach(label => {
    const {
      prediction_type,
      confidence,
      second_stage_reading,
      reading_stage,
      parsed_data,
    } = label;

    const canShowSecondStage = stage === "second_stage";

    const { bbox_coords, value } = canShowSecondStage ? second_stage_reading : label;

    if (bbox_coords?.length > 0 && value != null && !['text_recognition', 'text_recognition_postprocessed'].includes(reading_stage) && (stage === 'results' || canShowSecondStage)) {
      if (prediction_type !== 3) {
        ctx.lineWidth = '3';
        ctx.strokeStyle = predictionColors[prediction_type]?.color || theme.palette.secondary.main;

        if (!readingCanvas && bbox_coords && bbox_coords.length > 0) {
          drawPolygonWithVertexLabels(ctx, bbox_coords, factor, { showVertexNumbers });
        }

        ctx.font = '100% ' + theme.typography.fontFamily;
        ctx.fillStyle = predictionColors[prediction_type]?.color || theme.palette.secondary.main;
        var labelNumber = value;

        const bboxTextTransform = getBboxTextTransform(bbox_coords, factor);
        if (bboxTextTransform) {
          ctx.save();
          const {
            angle: bboxAngle,
            originX,
            originY,
            endX,
            textLength,
            crossLength,
          } = bboxTextTransform;

          var numberFormated = labelNumber;
          if (!disableText && readingCanvas) {
            const isPriceType = prediction_type === 0 || prediction_type === 'regular_price';
            const textToRender = isPriceType && format
              ? formatCurrency(labelNumber, '', currency_thousands_sep, currency_decimal_sep, currency_decimals)
              : labelNumber;

            const metrics = ctx.measureText(textToRender);
            const textWidth = metrics.width;
            const realTextHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
            const scaleX = textLength / textWidth;
            const scaleY = crossLength / realTextHeight;
            ctx.translate(originX, originY);
            ctx.rotate(bboxAngle);
            ctx.scale(scaleX, scaleY);
            ctx.textBaseline = 'top';
            ctx.fillText(textToRender, 0, 0);
          }
          ctx.restore();
          if (confidence && !disableText && !readingCanvas && splitView) {
            ctx.save();
            ctx.translate(
              originX,
              originY - 5,
            );
            ctx.rotate(bboxAngle);
            ctx.font = '100% ' + theme.typography.fontFamily;
            const textWidth = ctx.measureText(
              (confidence * 100).toFixed(2) + '%',
            ).width;
            if (
              endX + textWidth >
              canvas.width - margin
            ) {
              ctx.translate(-textWidth, 30);
            }
            ctx.fillText((confidence * 100).toFixed(2) + '%', 0, 0);
            ctx.restore();
          }
          if (value && !splitView) {
            numberFormated = labelNumber;
            if (prediction_type === 0) {
              if (format) {
                numberFormated = formatCurrency(labelNumber, currency_symbol, currency_thousands_sep, currency_decimal_sep, currency_decimals);
              }
            }
            ctx.save();
            ctx.translate(
              originX,
              originY - 5,
            );
            ctx.rotate(bboxAngle);
            ctx.font = '100% ' + theme.typography.fontFamily;
            ctx.fillText(numberFormated, 0, 0);
            ctx.restore();
          }
        }
      } else {
        if (label?.bbox_coords && bbox_coords.length > 0) {
          ctx.lineWidth = '3';
          ctx.strokeStyle = predictionColors[prediction_type]?.color || theme.palette.secondary.main;
          if (
            !readingCanvas &&
            bbox_coords &&
            bbox_coords.length > 0
          ) {
            drawPolygonWithVertexLabels(ctx, bbox_coords, factor, { showVertexNumbers });
          }
        }
        if (!showLabelText && readingCanvas) {
          let percentage = 100 * factor;
          ctx.font = `${percentage}%` + theme.typography.fontFamily;
          ctx.fillStyle = predictionColors[prediction_type]?.color || theme.palette.secondary.main;
          if (!disableText && bbox_coords && bbox_coords.length > 0) {
            ctx.save();
            drawTitleInBbox(ctx, bbox_coords, value, wordBboxIndex, factor);
            ctx.restore();
          }
        } else if (!splitView) {
          ctx.save();
          const bboxTextTransform = getSingleLineTextInBboxTransform(ctx, bbox_coords, value, factor, -20);
          if (bboxTextTransform) {
            const {
              angle: bboxAngle,
              originX,
              originY,
              realTextHeight,
              textLength,
              crossLength,
            } = bboxTextTransform;
            // !TODO: Fix hardcoded values. Maybe use a math function to calculate the text size
            const words = value.split(' ');
            let line = '';
            let y = 40;
            for (let word of words) {
              const proposedLine = `${line} ${word}`;
              if (ctx.measureText(proposedLine).width >= canvas.width - margin) {
                ctx.fillText(line, 20, y);
                line = word;
                y += 20;
              } else {
                line = proposedLine;
              }
            }

            const textWidth = ctx.measureText(line).width;

            ctx.translate(originX, originY);
            ctx.rotate(bboxAngle);

            const scaleY = crossLength / realTextHeight;
            const scale = textLength / textWidth;
            if (scaleY < 1) {
              ctx.scale(scale, 1);
            } else {
              ctx.scale(scale, scaleY);
            }
            ctx.textBaseline = 'middle';
            ctx.fillText(line, 0, realTextHeight / 2);
          }
          ctx.restore();
        }
      }
    } else if (!["results", "second_stage"].includes(stage) && stage === reading_stage) {
      ctx.lineWidth = '3';
      ctx.strokeStyle = predictionColors[(prediction_type ?? reading_stage)]?.color || theme.palette.secondary.main;
      ctx.fillStyle = predictionColors[(prediction_type ?? reading_stage)]?.color || theme.palette.secondary.main;
      parsed_data.forEach(item => {
        const { bboxes, texts } = item;
        if (!readingCanvas) {
          drawPolygonWithVertexLabels(ctx, bboxes, factor, { showVertexNumbers });
        } else {
          ctx.font = '100% ' + theme.typography.fontFamily;
          ctx.save();
          // Trust parsed word-box vertex order exactly like result boxes.
          const bboxTextTransform = getSingleLineTextInBboxTransform(ctx, bboxes, texts, factor);
          if (bboxTextTransform) {
            const {
              angle,
              originX,
              originY,
              realTextHeight,
              scaleX,
              scaleY,
            } = bboxTextTransform;

            ctx.translate(originX, originY);
            ctx.rotate(angle);

            if (scaleY / scaleX > 0.5) {
              ctx.scale(scaleX, scaleY);
            } else {
              ctx.scale(scaleX, scaleX);
            }
            ctx.textBaseline = 'middle';
            ctx.fillText(texts, 0, realTextHeight / 2);
          }
          ctx.restore();
        }
      })
    }
  });
};

export const formatCurrency = (value, currency_symbol, currency_thousands_sep, currency_decimal_sep, currency_decimals) => {
  if (value == null) {
    return '';
  }

  var integer = value.split('.')[0];
  var decimal = value.split('.')[1];
  integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, currency_thousands_sep);

  if (decimal) {
    decimal = decimal.substring(0, currency_decimals);
  }

  if (decimal && decimal.length < currency_decimals) {
    decimal += '0' * currency_decimals;
  }

  value = currency_symbol + integer;

  if (decimal) {
    value += currency_decimal_sep + decimal;
  }

  return value;
};

export const metersToPixels = (posMeters) => {
  const WALL_QUALITY_FACTOR = 2;
  const [xMeter, yMeter] = posMeters;
  const sc_rgb = 380.2270100911458 * WALL_QUALITY_FACTOR
  const xPixel = parseInt(sc_rgb * xMeter)
  const yPixel = parseInt(-sc_rgb * yMeter + 2100 * WALL_QUALITY_FACTOR / 2)
  return [xPixel, yPixel];
}

export const drawConsolidation = (metadataByStage, canvasId, theme, factor) => {
  const canvas = resolveCanvas(canvasId);
  if (!canvas) return;
  const ctx = canvas.getContext('2d');

  const sortedStageNames = Object.keys(metadataByStage).sort((a, b) => {
    const orderA = LABEL_DISPLAY[a]?.order ?? -1;
    const orderB = LABEL_DISPLAY[b]?.order ?? -1;
    return orderA - orderB;
  });

  sortedStageNames.forEach(stageName => {
    const stageConfig = LABEL_DISPLAY[stageName];
    if (!stageConfig) return;

    const [sizeX, sizeY] = stageConfig.size;
    const metadata = metadataByStage[stageName];

    metadata.forEach(item => {
      if (item.x !== undefined && item.y !== undefined && item.color) {
        const [pixelX, pixelY] = metersToPixels([item.x, item.y]);
        const [x1, y1] = [pixelX - sizeX / 2, pixelY - sizeY / 2];

        ctx.fillStyle = item.color;
        ctx.lineWidth = 4;
        ctx.fillRect(x1, y1, sizeX, sizeY);
      }
    });
  });
}

export const IMAGE_DOWNLOAD_TYPES = {
  RAW: 'raw',
  WITH_OBJECTS: 'with_objects',
};

/**
 * Builds the download context of the displayed image, or null when no image is loaded.
 * Images may be loaded from a session other than the selected one (e.g. a detection
 * found by SKU), so their own session is used; consolidation walls are always loaded
 * with the selected session.
 */
export const buildDownloadContext = ({ ai, store, selectedSession, images, imagesSession, imageIndex, consolidationStages }) => {
  const isConsolidation = ai === 'Consolidation';
  const image = isConsolidation ? consolidationStages?.image?.[0] : images?.image?.[imageIndex];
  if (!image?.image_url) return null;

  return {
    store,
    session: isConsolidation ? selectedSession : imagesSession,
    blobPath: image.image_url,
    imageId: image.id,
  };
};

// Same flow as Overseer's layout download: the image is drawn on an offscreen
// canvas, the overlay is drawn on top and the result is saved as a PNG file.
export const downloadCanvasAsPNG = ({ fileName, imageSrc, factor = 1, drawOverlay }) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth * factor;
      canvas.height = img.naturalHeight * factor;
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      drawOverlay?.(canvas);

      const link = document.createElement('a');
      link.download = fileName;
      link.href = canvas.toDataURL('image/png');
      link.click();
      resolve();
    };
    img.onerror = reject;
    img.src = imageSrc;
  });
